import { defaultDeckSurfaceHex } from '@flashka/shared/deck/defaults';
import { useTheme } from '@flashka/shared/ui';

import { useCallback } from 'react';

/**
 * Resolve a stored deck colour (custom hex OR legacy named colour) to the
 * concrete hex the shared DS `DeckCard` needs — its emoji-tile tint and deck
 * glyph are computed from a hex, not Tailwind classes.
 *
 * Named values mirror the light-mode `--deck-{name}-rgb` palette in
 * `index.css` (the same hexes as shared `AVATAR_BG_COLORS`); legacy remaps
 * (`violet`→fuchsia, `yellow`→amber) follow `deckColorMap` in
 * `components/icons/deck.tsx`. Identity colours are mode-invariant by DS
 * design, so the light palette is the single source.
 */
const NAMED_DECK_HEX: Record<string, string> = {
  orange: '#ff8904',
  amber: '#ffb900',
  lime: '#9ae600',
  green: '#05df72',
  emerald: '#00d492',
  teal: '#00d5be',
  cyan: '#00d3f3',
  sky: '#00bcff',
  blue: '#51a2ff',
  fuchsia: '#ed6aff',
  pink: '#fb64b6',
  rose: '#ff637e',
  // Legacy remaps
  violet: '#ed6aff',
  yellow: '#ffb900',
};

/**
 * The curated default (`DEFAULT_DECK_COLOR`, which is not in the map above)
 * and any uncoloured/unknown deck — `secondary-100`, matching the "Default"
 * tile that leads the colour grid.
 *
 * Unlike the identity colours above, this one is NOT mode-invariant: it is a
 * SURFACE, not an identity, so it follows the secondary ramp's dark mirror
 * (`secondary-900`) like every other neutral. A flat `#f5f5f5` would paint a
 * near-white slab onto the `#090909` dark page.
 */
export function deckColorHex(color: string | null | undefined, isDark = false): string {
  const neutral = defaultDeckSurfaceHex(isDark);
  if (!color) return neutral;
  if (color.startsWith('#')) return color;
  return NAMED_DECK_HEX[color] ?? neutral;
}

/**
 * Theme-bound {@link deckColorHex} — what anything that RENDERS a DS card
 * should use, so the neutral default tracks the theme. The bare function is
 * only correct where the result can't reach a dark surface.
 */
export function useDeckColorHex() {
  const { isDark } = useTheme();
  return useCallback(
    (color: string | null | undefined) => deckColorHex(color, isDark),
    [isDark],
  );
}
